import {
  useArchiveTask,
  useCreateHabitAllowed,
  useDeleteTask,
  useDuplicateTask,
} from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArchiveDocumentsBoxBig16,
  CopyDuplicateObject16,
  Trash16,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenDeleteHabitDialog } from 'shared/contexts/delete-habit';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { Habit } from 'shared/types/habit';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';

export type RemoveListProps = {
  task: Task;
  onClose: () => void;
  hasListBelow?: boolean;
};

export const LifecycleList: React.FC<RemoveListProps> = ({
  task,
  onClose,
  hasListBelow,
}) => {
  const { t } = useTranslation();
  const archiveTask = useArchiveTask();
  const duplicateTask = useDuplicateTask();
  const isCreateHabitAllowed = useCreateHabitAllowed();
  const openPremiumDialog = useOpenPremiumDialog();

  const parentTaskId = useMemo(
    () => task.parentIds?.[task.parentIds.length - 1],
    [task],
  );

  const { submit: deleteTask } = useDeleteTask(parentTaskId);
  const deleteHabit = useOpenDeleteHabitDialog();

  const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);
  const isDuplicateAllowed =
    isCreateHabitAllowed || task.type !== TaskType.Habit;

  const onDuplicate = () => {
    if (!isDuplicateAllowed) {
      openPremiumDialog();
      onClose();
      return;
    }

    duplicateTask(task);
    onClose();
  };

  const onArchive = () => {
    archiveTask(task);
    onClose();
  };

  const onDelete = () => {
    if (isHabit) {
      deleteHabit(task as Habit);
      onClose();
      return;
    }

    deleteTask(task);
    onClose();
  };

  return (
    <PopupMenuList hasBottomBorder={hasListBelow}>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onDuplicate}
          start={<Icon icon={CopyDuplicateObject16} />}
        >
          {t('context-menus.task.labels.duplicate')}
        </PopupMenuButton>
      </PopupMenuListItem>
      {isHabit && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onArchive}
            start={<Icon icon={ArchiveDocumentsBoxBig16} />}
          >
            {t(
              task.archivedAt
                ? 'context-menus.task.labels.de-archive'
                : 'context-menus.task.labels.archive',
            )}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
      <PopupMenuListItem>
        <PopupMenuButton onClick={onDelete} start={<Icon icon={Trash16} />}>
          {t('context-menus.task.labels.delete')}
        </PopupMenuButton>
      </PopupMenuListItem>
    </PopupMenuList>
  );
};
